import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, MoreVertical, Edit2, Trash2, FolderPlus } from 'lucide-react';

/**
 * Recursive folder tree with click to select, expand/collapse, and
 * context menu (rename/delete/add subfolder).
 */
export default function FolderTree({
  folders,
  selectedId,
  onSelect,
  onCreateSubfolder,
  onRename,
  onDelete,
  canEdit,
}) {
  const [expanded, setExpanded] = useState(() => new Set());

  const childrenOf = (parentId) =>
    folders
      .filter(f => (f.parent_id || null) === parentId)
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

  const toggle = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const renderNode = (folder, depth) => {
    const kids = childrenOf(folder.id);
    const isOpen = expanded.has(folder.id);
    const isSelected = folder.id === selectedId;
    return (
      <div key={folder.id}>
        <FolderRow
          folder={folder}
          depth={depth}
          isOpen={isOpen}
          hasChildren={kids.length > 0}
          isSelected={isSelected}
          onToggle={() => toggle(folder.id)}
          onSelect={() => onSelect(folder.id)}
          onCreateSubfolder={onCreateSubfolder}
          onRename={onRename}
          onDelete={onDelete}
          canEdit={canEdit}
        />
        {isOpen && kids.map(k => renderNode(k, depth + 1))}
      </div>
    );
  };

  const roots = childrenOf(null);

  return (
    <div className="space-y-0.5" data-testid="folder-tree">
      <div
        onClick={() => onSelect('root')}
        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${
          selectedId === 'root' ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
        }`}
        data-testid="root-folder"
      >
        <FolderOpen size={14} />
        <span>Root</span>
      </div>
      {roots.map(f => renderNode(f, 0))}
    </div>
  );
}

function FolderRow({
  folder, depth, isOpen, hasChildren, isSelected,
  onToggle, onSelect, onCreateSubfolder, onRename, onDelete, canEdit,
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className={`group flex items-center gap-1 pr-1 py-1 rounded cursor-pointer text-sm ${
        isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
      }`}
      style={{ paddingLeft: `${depth * 14 + 4}px` }}
      data-testid={`folder-node-${folder.id}`}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className="p-0.5 text-gray-400 hover:text-gray-700"
      >
        {hasChildren ? (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : <span className="w-3 inline-block" />}
      </button>
      <div onClick={onSelect} className="flex items-center gap-1.5 flex-1 min-w-0">
        {isOpen ? <FolderOpen size={13} className="text-blue-500" /> : <Folder size={13} className="text-blue-500" />}
        <span className="truncate">{folder.name}</span>
      </div>
      {canEdit && (
        <div className="relative opacity-0 group-hover:opacity-100">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
            className="p-0.5 text-gray-400 hover:text-gray-700"
            data-testid={`folder-menu-${folder.id}`}
          >
            <MoreVertical size={12} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }} />
              <div className="absolute right-0 top-full mt-1 z-20 w-44 bg-white border rounded-lg shadow-lg py-1 text-sm">
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onCreateSubfolder(folder.id); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-gray-50 text-gray-700"
                >
                  <FolderPlus size={12} /> Add subfolder
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onRename(folder); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-gray-50 text-gray-700"
                >
                  <Edit2 size={12} /> Rename
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(folder); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-gray-50 text-red-600"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
